import React, { useState } from "react";
import { connect, useSelector } from "react-redux";
import { PageHeader } from "@ant-design/pro-components";
import { Layout, Breadcrumb, Button, ConfigProvider, Avatar, Card } from "antd";
import { ProTable } from "@ant-design/pro-components";
import { FileSyncOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import ptBRIntl from "antd/lib/locale/pt_BR";
import moment from "moment";
import "moment/locale/pt-br";
import "reactjs-popup/dist/index.css";

/**
 * Actions
 */
import { allActivitiesForms } from "auth/actions/activitieActions";

/**
 * Components
 */
import TopBar from "components/NavBar/TopBar";
import DrawerSider from "components/NavBar/DrawerSider";
import { LayoutTopSide } from "components/NavBar/styles";

/**
 * Utils
 */
import { getUsers } from "../../../auth/actions/userActions";
import { allFormsOfSavedObjects } from "../../../auth/actions/objActions";
import { resolveUserNames } from "utils/resolveUserNameActivity";
import { defaultTablePagination } from "utils/table-pagination";
import { ItemActivityForm } from "./ItemActivityForm";

const actions = {
    created: "Criou",
    updated: "Atualizou",
    archived: "Arquivou",
    deleted: 'Deletou',
    sorted: "Ordenou"
};

const entities = {
    'field': "Campo",
    'form': "Formulário",
    'category': "Categoria"
};

const getActionText = (text) => {
    return actions[text] || text;
};

const getEntityText = (text) => {
    return entities[text] || text;
};


function AllActivitiesForms({ user }) {
    const company = useSelector((state) => state.user.company);
    const navigate = useNavigate();
    const [userNames, setUserNames] = useState(new Map());

    const routes = [
        {
            breadcrumbName: "Atividades de Formulários",
            href: "/atividades/formularios/todas",
        },
        {
            breadcrumbName: "todas",
        },
    ];

    const requestUsers = async (values) => {
        let data;
        if (values.keyWords) {
            data = await getUsers(values.keyWords, company.unitId);
        } else {
            data = await getUsers(null, company.unitId);
        }
        data = data.users.map((item) => ({
            label: item.firstName,
            value: item.uuid,
        }));

        return data;
    }

    const requestForms = async (values) => {
        let data;
        if (values.keyWords) {
            data = await allFormsOfSavedObjects(values.keyWords, company.unitId);
        } else {
            data = await allFormsOfSavedObjects(null, company.unitId);
        }
        data = data.message.map((item) => ({
            label: item.name,
            value: item.name,
        }));
        return data;
    }

    const columns = [
        {
            sorter: true,
            title: "Formulário",
            dataIndex: "form",
            key: "form",
            render: (dom, record, index, action, schema) => {
                const text = record[schema.dataIndex];
                if (text) {
                    return (
                        <>
                            <Avatar
                                size="small"
                                icon={<FileSyncOutlined />}
                                style={{
                                    backgroundColor: "#0e9c84",
                                }}
                            />
                            <Button
                                type="link"
                                style={{
                                    color: "#0e9c84",
                                }}
                                onClick={() => navigate(`/formularios/registros/obj_${text}`)}
                            >
                                {text.replace("obj_", "")}
                            </Button>
                        </>
                    );
                }
                return "N/A";
            },
            fieldProps: {
                mode: "multiple",
                placeholder: "Selecione o formulário",
                showSearch: true,
                allowClear: true,
            },
            width: 200,
            ellipsis: true,
            valueType: "select",
            request: requestForms
        },
        {
            sorter: true,
            title: "Ação",
            dataIndex: "action",
            key: "action",
            render: (text) => getActionText(text),
            valueEnum: actions,
            valueType: "select",
            fieldProps: {
                mode: "multiple",
                placeholder: "Selecione a ação",
                allowClear: true,
            },
            width: 200,
            ellipsis: true,
        },
        {
            sorter: true,
            title: "Entidade",
            dataIndex: "entity",
            key: "entity",
            responsive: ["md"],
            render: (text) => getEntityText(text),
            valueEnum: entities,
            valueType: "select",
            fieldProps: {
                mode: "multiple",
                placeholder: "Selecione a entidade",
                allowClear: true,
            },
            width: 200,
            ellipsis: true,
        },
        {
            sorter: true,
            title: "Autor",
            dataIndex: "createdBy",
            key: "createdBy",
            responsive: ["md"],
            valueType: "select",
            fieldProps: {
                mode: "multiple",
                placeholder: "Selecione o autor",
                showSearch: true,
                allowClear: true,
            },
            width: 200,
            ellipsis: true,
            request: requestUsers,
            render: (dom, record, index, action, schema) => {
                const text = record[schema.dataIndex];
                return userNames.get(text) || text || "N/A";
            }
        },
        {
            sorter: true,
            title: "Criado em",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (dom, entity, index, action, schema) => {
                if (action) {
                    return moment(entity[schema.dataIndex], "YYYY-MM-DDTHH:mm:ss[Z]").format("DD/MM/YYYY HH:mm");
                }
            },
            valueType: "dateTimeRange",
            fieldProps(form, config) {
                return {
                    ...config,
                    format: "DD/MM/YYYY HH:mm",
                    placeholder: ["Data inicial", "Data final"],
                    allowClear: true,
                };
            },
            width: 200,
            ellipsis: true,
        }

    ];


    return (
        <Layout>
            <LayoutTopSide>
                <TopBar />
                <Layout style={{ overflow: "hidden", flex: 1 }}>
                    <DrawerSider />
                    <Layout
                        style={{
                            padding: "0 24px 24px",
                            height: "100%",
                            overflowY: "auto",
                        }}
                    >
                        <PageHeader
                            style={{ margin: "16px 0 5px 0" }}
                            title="Lista de atividades"
                            subTitle="Acompanhe o lançamento, edição e exclusão de qualquer formulário, categoria ou campo."
                            onBack={() => window.history.back()}
                            breadcrumb={
                                <Breadcrumb>
                                    {routes.map((a) =>
                                        a.hasOwnProperty("href") ? (
                                            <Breadcrumb.Item key={a.breadcrumbName}>
                                                <a onClick={() => navigate(a.href)}>
                                                    {a.breadcrumbName}
                                                </a>
                                            </Breadcrumb.Item>
                                        ) : (
                                            <Breadcrumb.Item key={a.breadcrumbName}>{a.breadcrumbName}</Breadcrumb.Item>
                                        )
                                    )}
                                </Breadcrumb>
                            }
                        />
                        <Card>
                            <ConfigProvider locale={ptBRIntl}>
                                <ProTable
                                    search={{
                                        labelWidth: 90,
                                        defaultCollapsed: true,
                                        searchText: "Filtrar",
                                        resetText: "Limpar",
                                    }}
                                    pagination={defaultTablePagination}
                                    rowKey="_id"
                                    type="table"
                                    request={async (params = {}, sorter, filter) => {
                                        const items = await allActivitiesForms({
                                            form: params.form,
                                            action: params.action,
                                            entity: params.entity,
                                            createdAt: params.createdAt,
                                            createdBy: params.createdBy,
                                            page: params.current,
                                            pageSize: params.pageSize,
                                            sorter,
                                            companyId: company?.companyId != null ? String(company.companyId) : null,
                                            unitId: company?.unitId != null ? String(company.unitId) : null
                                        });

                                        const data = items?.message ?? [];

                                        // Resolve user names for this page (uuid -> name), like data activities
                                        const ids = data
                                            .map((a) => a.createdBy)
                                            .filter((id) => id && id !== "System");
                                        if (ids.length > 0) {
                                            try {
                                                const map = await resolveUserNames(ids, company?.unitId);
                                                setUserNames((prev) => {
                                                    const merged = new Map(prev);
                                                    map.forEach((val, key) => merged.set(key, val));
                                                    return merged;
                                                });
                                            } catch (error) {
                                                console.error("Failed to resolve user names", error);
                                            }
                                        }

                                        return {
                                            data,
                                            success: items?.success !== false,
                                            total: items?.pagination?.totalHits ?? 0,
                                        };
                                    }}
                                    columns={columns}
                                    dateFormatter={"number"}
                                    scroll={{ x: 'max-content' }}
                                    expandable={{
                                        expandedRowRender: ItemActivityForm
                                    }}
                                    style={{ padding: 0 }}
                                />
                            </ConfigProvider>
                        </Card>
                    </Layout>
                </Layout>
            </LayoutTopSide>
        </Layout>
    );
}

const mapStateToProps = ({ session }) => ({
    user: session.user,
});

export default connect(mapStateToProps)(AllActivitiesForms);
